import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { MapPin, Phone, Mail, Send, CheckCircle } from "lucide-react";
import { contactAPI, contentAPI } from "../services/api";
import { useLanguage } from "../hooks/useLanguage";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const loadRecaptchaScript = (siteKey) => {
  if (typeof window === "undefined" || !siteKey || window.grecaptcha) return Promise.resolve();
  return new Promise((resolve) => {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      if (window.grecaptcha) {
        window.grecaptcha.ready(resolve);
      } else {
        resolve();
      }
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => {
      if (window.grecaptcha) window.grecaptcha.ready(resolve);
      else resolve();
    };
    document.head.appendChild(script);
  });
};

const getRecaptchaToken = (action = "submit") => {
  if (!RECAPTCHA_SITE_KEY || typeof window === "undefined" || !window.grecaptcha) return Promise.resolve(null);
  return loadRecaptchaScript(RECAPTCHA_SITE_KEY).then(() => {
    return new Promise((resolve) => {
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(() => resolve(null));
    });
  });
};

const ContactSection = () => {
  const currentLanguage = useLanguage();
  const recaptchaLoaded = useRef(false);
  const [sectionData, setSectionData] = useState(null);
  const [formData, setFormData] = useState({
    pflegegrad: "",
    anrede: "",
    nachname: "",
    vorname: "",
    krankenkasse: "",
    versichertennummer: "",
    adresse_strasse: "",
    adresse_plz_ort: "",
    email: "",
    telefon: "",
    wunschtermin: "",
    geburtsdatum: "",
    hausarzt: "",
    nachricht: "",
    consent: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const sections = await contentAPI.getSections("home").catch(() => []);
        const contactSection = sections.find(s => s.section_type === "contact");
        if (contactSection?.content_data) {
          setSectionData(contactSection.content_data);
        }
      } catch (error) {
        console.error("Error fetching contact section:", error);
      }
    };
    fetchSectionData();
  }, []);

  useEffect(() => {
    if (RECAPTCHA_SITE_KEY && !recaptchaLoaded.current) {
      recaptchaLoaded.current = true;
      loadRecaptchaScript(RECAPTCHA_SITE_KEY);
    }
  }, []);

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value[currentLanguage] || value['de'] || value[Object.keys(value)[0]] || fallback;
    }
    return String(value) || fallback;
  };

  const sectionTitle = sectionData ? getStringValue(sectionData.title, "Kontakt") : "Kontakt";
  const sectionDescription = sectionData ? getStringValue(sectionData.description, "Wir sind für Sie da. Kontaktieren Sie uns für eine kostenlose Beratung.") : "Wir sind für Sie da. Kontaktieren Sie uns für eine kostenlose Beratung.";
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const isWeekday = (dateStr) => {
    if (!dateStr) return true;
    const d = new Date(dateStr + "T12:00:00");
    const day = d.getDay();
    return day >= 1 && day <= 5;
  };

  const handleWunschterminChange = (value) => {
    setDateError("");
    if (!value) {
      handleInputChange("wunschtermin", "");
      return;
    }
    if (!isWeekday(value)) {
      setDateError("Bitte wählen Sie nur Werktage (Montag–Freitag).");
      handleInputChange("wunschtermin", "");
      return;
    }
    handleInputChange("wunschtermin", value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      alert("Bitte stimmen Sie der Datenschutzerklärung zu.");
      return;
    }
    if (formData.wunschtermin && !isWeekday(formData.wunschtermin)) {
      setDateError("Bitte wählen Sie nur Werktage (Montag–Freitag).");
      return;
    }

    setSubmitting(true);
    
    try {
      if (!RECAPTCHA_SITE_KEY) {
        alert("Spam-Schutz ist nicht konfiguriert. Bitte wenden Sie sich an den Betreiber der Website.");
        setSubmitting(false);
        return;
      }
      const captchaToken = await getRecaptchaToken("submit");
      if (!captchaToken) {
        alert("Überprüfung (CAPTCHA) konnte nicht geladen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.");
        setSubmitting(false);
        return;
      }

      const submissionData = {
        name: `${formData.anrede} ${formData.vorname} ${formData.nachname}`.trim(),
        email: formData.email,
        phone: formData.telefon,
        message: `Beratungsbesuch nach §37 Abs. 3 SGB XI

Patientendaten:
- Pflegegrad: ${formData.pflegegrad || 'Nicht angegeben'}
- Anrede: ${formData.anrede || 'Nicht angegeben'}
- Name: ${formData.vorname} ${formData.nachname}
- Krankenkasse: ${formData.krankenkasse || 'Nicht angegeben'}
- Versichertennummer: ${formData.versichertennummer || 'Nicht angegeben'}
- Adresse: ${formData.adresse_strasse || 'Nicht angegeben'}, ${formData.adresse_plz_ort || 'Nicht angegeben'}
- E-Mail: ${formData.email}
- Telefon: ${formData.telefon || 'Nicht angegeben'}
- Wunschtermin: ${formData.wunschtermin || 'Nicht angegeben'}
- Geburtsdatum: ${formData.geburtsdatum || 'Nicht angegeben'}
- Hausarzt: ${formData.hausarzt || 'Nicht angegeben'}

Nachricht:
${formData.nachricht || 'Keine Nachricht'}`,
        inquiry_type: "beratungsbesuch",
        company: "",
        budget: "",
        service: "",
        timeline: ""
      };
      if (captchaToken) submissionData.captcha_token = captchaToken;

      await contactAPI.submit(submissionData);
      setIsSubmitted(true);
      setFormData({
        pflegegrad: "",
        anrede: "",
        nachname: "",
        vorname: "",
        krankenkasse: "",
        versichertennummer: "",
        adresse_strasse: "",
        adresse_plz_ort: "",
        email: "",
        telefon: "",
        wunschtermin: "",
        geburtsdatum: "",
        hausarzt: "",
        nachricht: "",
        consent: false
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      const detail = error.response?.data?.detail;
      const isCaptcha = typeof detail === "string" && (detail.includes("CAPTCHA") || detail.includes("captcha"));
      const message = isCaptcha
        ? "CAPTCHA-Überprüfung fehlgeschlagen. Bitte versuchen Sie es erneut."
        : (detail || "Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-[var(--vortex-bg)]">
      <div className="max-w-7xl mx-auto w-full min-w-0">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-[var(--vortex-text-primary)]">
            {sectionTitle}
          </h2>
          <p className="text-base sm:text-xl text-[var(--vortex-text-secondary)] max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        {/* Contact Form - In Card */}
        <div className="mb-8 sm:mb-12">
          <div className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-8 lg:p-10 rounded-xl border-2 border-[var(--vortex-border)] shadow-lg">
            {/* Form Header */}
            <div className="mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 text-[var(--vortex-text-primary)]">
                Termin für Beratungsbesuch nach §37 Abs. 3 SGB XI
              </h3>
              <p className="text-base md:text-lg text-[var(--vortex-text-secondary)]">
                Vereinbaren Sie einen Termin für Beratungsbesuche nach §37,3.
              </p>
            </div>

            {isSubmitted ? (
              <div className="bg-[var(--vortex-health)]/10 border-2 border-[var(--vortex-health)] p-6 rounded-xl">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-[var(--vortex-health)]" />
                  <p className="text-lg text-[var(--vortex-text-primary)] font-semibold">
                    Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Form Fields in 2-column grid */}
                <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Pflegegrad */}
                  <div>
                    <label htmlFor="pflegegrad" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Pflegegrad
                    </label>
                    <Select
                      value={formData.pflegegrad || undefined}
                      onValueChange={(value) => handleInputChange("pflegegrad", value)}
                    >
                      <SelectTrigger className="w-full text-lg min-h-[48px] bg-[var(--vortex-bg)] border-[var(--vortex-border)] text-[var(--vortex-text-primary)]">
                        <SelectValue placeholder="- auswählen -" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--vortex-surface)] border-[var(--vortex-border)]">
                        <SelectItem value="Pflegegrad 1" className="cursor-pointer">Pflegegrad 1</SelectItem>
                        <SelectItem value="Pflegegrad 2" className="cursor-pointer">Pflegegrad 2</SelectItem>
                        <SelectItem value="Pflegegrad 3" className="cursor-pointer">Pflegegrad 3</SelectItem>
                        <SelectItem value="Pflegegrad 4" className="cursor-pointer">Pflegegrad 4</SelectItem>
                        <SelectItem value="Pflegegrad 5" className="cursor-pointer">Pflegegrad 5</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Anrede */}
                  <div>
                    <label htmlFor="anrede" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Anrede
                    </label>
                    <Select
                      value={formData.anrede || undefined}
                      onValueChange={(value) => handleInputChange("anrede", value)}
                    >
                      <SelectTrigger className="w-full text-lg min-h-[48px] bg-[var(--vortex-bg)] border-[var(--vortex-border)] text-[var(--vortex-text-primary)]">
                        <SelectValue placeholder="- auswählen -" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--vortex-surface)] border-[var(--vortex-border)]">
                        <SelectItem value="Frau" className="cursor-pointer">Frau</SelectItem>
                        <SelectItem value="Herr" className="cursor-pointer">Herr</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Nachname */}
                  <div>
                    <label htmlFor="nachname" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Nachname *
                    </label>
                    <Input
                      id="nachname"
                      type="text"
                      required
                      value={formData.nachname}
                      onChange={(e) => handleInputChange("nachname", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="Nachname"
                    />
                  </div>

                  {/* Vorname */}
                  <div>
                    <label htmlFor="vorname" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Vorname *
                    </label>
                    <Input
                      id="vorname"
                      type="text"
                      required
                      value={formData.vorname}
                      onChange={(e) => handleInputChange("vorname", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="Vorname"
                    />
                  </div>

                  {/* Krankenkasse */}
                  <div>
                    <label htmlFor="krankenkasse" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Krankenkasse
                    </label>
                    <Input
                      id="krankenkasse"
                      type="text"
                      value={formData.krankenkasse}
                      onChange={(e) => handleInputChange("krankenkasse", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="Krankenkasse"
                    />
                  </div>

                  {/* Versichertennummer */}
                  <div>
                    <label htmlFor="versichertennummer" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Versichertennummer
                    </label>
                    <Input
                      id="versichertennummer"
                      type="text"
                      value={formData.versichertennummer}
                      onChange={(e) => handleInputChange("versichertennummer", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="Versichertennummer"
                    />
                  </div>

                  {/* Adresse Straße */}
                  <div>
                    <label htmlFor="adresse_strasse" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Adresse Straße, Nr.
                    </label>
                    <Input
                      id="adresse_strasse"
                      type="text"
                      value={formData.adresse_strasse}
                      onChange={(e) => handleInputChange("adresse_strasse", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="Straße, Hausnummer"
                    />
                  </div>

                  {/* Adresse PLZ, Ort */}
                  <div>
                    <label htmlFor="adresse_plz_ort" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Patienten Adresse PLZ, Ort
                    </label>
                    <Input
                      id="adresse_plz_ort"
                      type="text"
                      value={formData.adresse_plz_ort}
                      onChange={(e) => handleInputChange("adresse_plz_ort", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="PLZ, Ort"
                    />
                  </div>

                  {/* E-Mail */}
                  <div>
                    <label htmlFor="email" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      E-Mail *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="ihre.email@beispiel.de"
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label htmlFor="telefon" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Telefon *
                    </label>
                    <Input
                      id="telefon"
                      type="tel"
                      required
                      value={formData.telefon}
                      onChange={(e) => handleInputChange("telefon", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="02133-21 84 111"
                    />
                  </div>


                  {/* Geburtsdatum */}
                  <div>
                    <label htmlFor="geburtsdatum" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Geburtsdatum
                    </label>
                    <Input
                      id="geburtsdatum"
                      type="date"
                      value={formData.geburtsdatum}
                      onChange={(e) => handleInputChange("geburtsdatum", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  {/* Hausarzt */}
                  <div>
                    <label htmlFor="hausarzt" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Hausarzt
                    </label>
                    <Input
                      id="hausarzt"
                      type="text"
                      value={formData.hausarzt}
                      onChange={(e) => handleInputChange("hausarzt", e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      placeholder="Name des Hausarztes"
                    />
                  </div>

                  {/* Wunschtermin */}
                  <div>
                    <label htmlFor="wunschtermin" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                      Ihr Wunschtermin
                    </label>
                    <Input
                      id="wunschtermin"
                      type="date"
                      value={formData.wunschtermin}
                      onChange={(e) => handleWunschterminChange(e.target.value)}
                      className="w-full text-lg min-h-[48px]"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    {dateError && (
                      <p className="mt-1.5 text-sm text-red-600" role="alert">{dateError}</p>
                    )}
                  </div>

                </div>

                {/* Nachricht - Full width */}
                <div className="md:col-span-2">
                  <label htmlFor="nachricht" className="block text-base font-semibold text-[var(--vortex-text-primary)] mb-2">
                    Nachricht *
                  </label>
                  <Textarea
                    id="nachricht"
                    required
                    value={formData.nachricht}
                    onChange={(e) => handleInputChange("nachricht", e.target.value)}
                    className="w-full text-lg min-h-[120px]"
                    placeholder="Ihre Nachricht..."
                    rows={5}
                  />
                </div>

                {/* Consent Checkbox - Full width */}
                <div className="md:col-span-2 flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={formData.consent}
                    onChange={(e) => handleCheckboxChange("consent", e.target.checked)}
                    required
                    className="mt-1 w-[10px] h-[10px] text-[var(--vortex-accent-primary)] border-[var(--vortex-border)] rounded focus:ring-[var(--vortex-accent-primary)] cursor-pointer"
                  />
                  <label htmlFor="consent" className="text-sm text-[var(--vortex-text-secondary)] leading-relaxed flex-1">
                    Ich erkläre mich mit der Verarbeitung der eingegebenen Daten sowie der Datenschutzerklärung einverstanden. <span className="text-[var(--vortex-accent-primary)] font-semibold">*</span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <p className="text-sm text-[var(--vortex-text-muted)] mb-4">
                    <span className="text-[var(--vortex-accent-primary)]">*</span> Pflichtfelder
                  </p>

                  <Button
                    type="submit"
                    disabled={submitting || !formData.consent}
                    className="btn-primary w-full py-4 text-lg font-semibold"
                  >
                    {submitting ? (
                      "Wird gesendet..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Termin anfragen
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
